import { z } from 'zod';
import { ZPlatform } from './platform.js';
const ZCartTaxLine = z.object({
    title: z.string().optional(),
    amount: z.number(),
    rate: z.number(),
});
const ZCartDiscount = z.object({
    code: z.string().optional(),
    type: z.string(),
    amount: z.number(),
    target: z.string().optional(),
});
const ZCartShippingMethod = z.object({
    id: z.string(),
    title: z.string(),
    amount: z.number(),
    currency: z.string().max(3),
    taxLine: z.union([ZCartTaxLine.array(), ZCartTaxLine]).optional(),
});
const ZCartDuties = z.object({
    amount: z.number(),
    taxLines: ZCartTaxLine.array().optional(),
});
const ZCartLineItem = z.object({
    title: z.string(),
    name: z.string(),
    productId: z.string().optional(),
    sku: z.string().optional(),
    amount: z.number(),
    quantity: z.number().int().positive(),
    requiresShipping: z.boolean().default(true),
    imageUrl: z.string().optional(),
    variantId: z.string().optional(),
    variantTitle: z.string().optional(),
    properties: z
        .object({ name: z.string(), value: z.string(), type: z.enum(['text', 'image']).optional() })
        .array()
        .optional(),
    taxLines: ZCartTaxLine.array().optional(),
    duties: ZCartDuties.array().optional(),
    discounts: ZCartDiscount.array().optional(),
});
const ZCartBillingAddress = z.object({
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().optional(),
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    zip: z.string(),
    province: z.string().optional(),
    provinceCode: z.string().optional(),
    country: z.string(),
    countryCode: z.string().optional(),
});
const ZCartShippingAddress = z.object({
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().optional(),
    address1: z.string(),
    address2: z.string().optional(),
    city: z.string(),
    zip: z.string(),
    province: z.string().optional(),
    provinceCode: z.string().optional(),
    country: z.string(),
    countryCode: z.string().optional(),
});
const ZCartPaymentSource = z.object({
    id: z.string(),
    userId: z.string(),
    name: z.string(),
    type: z.string(),
    isDefault: z.boolean(),
    brand: z.string(),
    funding: z.string().optional(),
    country: z.string(),
    last4: z.number().int().max(9999),
    expMonth: z.number().int().max(12).positive(),
    expYear: z.number().int().positive(),
});
const ZCartStatus = z.enum(['pending', 'processed', 'cancelled']);
export const ZCartPaymentServiceProvider = z.enum(['stripe']);
export const ZPlatformCart = z.object({
    platformId: z.string(),
    platform: ZPlatform,
    billingAddress: ZCartBillingAddress,
    shippingAddress: ZCartShippingAddress,
    shippingMethod: ZCartShippingMethod,
    discounts: ZCartDiscount.array(),
    lineItems: ZCartLineItem.array(),
    paymentSource: ZCartPaymentSource.optional(),
    giftWrapping: z.number().optional(),
    totalAmount: z.number(),
    totalWeight: z.number().optional(),
    subTotal: z.number().optional(),
    currency: z.string().max(3),
    totalTax: z.number(),
    duties: ZCartDuties.array().optional(),
    totalDuties: z.number().optional(),
    taxIncluded: z.boolean(),
    taxLines: ZCartTaxLine.array(),
    metadata: z.record(z.string(), z.string()).optional(),
    /** Marketing attribution data collected from the merchant's website (click IDs, UTMs, cookies) */
    analytics: z.record(z.string(), z.string()).optional(),
    /** Checkout session context for internal analytics (PostHog) — shopName, cart details, theme, etc. */
    checkoutContext: z.record(z.string(), z.unknown()).optional(),
    note: z.string().optional().nullable(),
    /** Used in Shopify in order to identify the draft order to convert it to a real order */
    orderSlipId: z.string().optional(),
    /** Cart fingerprint for staleness detection (hash of cart state at draft creation) */
    cartFingerprint: z.string().optional(),
});
export const ZCart = ZPlatformCart.extend({
    id: z.string().uuid(),
    shopId: z.string(),
    customerId: z.string(),
    intentId: z.string(),
    requestId: z.string(),
    psp: ZCartPaymentServiceProvider,
    status: ZCartStatus,
    fee: z.number(),
    createdAt: z.date(),
    updatedAt: z.date(),
    paymentMethod: z.string().optional(),
    cancellationReason: z.enum(['outOfStock', 'shippingUnavailable']).optional(),
    cancellationDetails: z.array(z.string()).optional(),
});
//# sourceMappingURL=cart.js.map